/**
 * Google Drive Token Refresh Service
 * Automatically refreshes tokens using stored refresh token from localStorage when Google APIs return 401 responses
 */

class GoogleTokenRefreshService {
  constructor() {
    this.isRefreshing = false
    this.refreshPromise = null
    this.maxRetries = 3
    this.minRefreshInterval = 15 * 60 * 1000 // 15 minutes minimum between refreshes (increased from 1 second)
    this.maxRefreshAttempts = 3 // Max 3 attempts within the window (reduced from 20)
    this.refreshWindow = 60 * 60 * 1000 // 1 hour window (increased from 1 minute)
    this.refreshAttempts = [] // Array of refresh attempt timestamps
    this.lastRefreshTime = null
    
    // Initialize token properties from localStorage
    this.accessToken = localStorage.getItem('cloudAccessToken') || null
    this.refreshToken = localStorage.getItem('cloudRefreshToken') || null
    
    // Initialize counters and limits
    this.retryCount = 0
    this.refreshCount = 0
    this.maxRefreshPerMinute = 3 // Reduced from 20 to be more respectful
    this.refreshHistory = []
    
    // Initialize monitoring state
    this.monitoringStarted = false
    this.originalSetItem = null
    this.originalRemoveItem = null
    
    // Only start monitoring if user is authenticated and not on onboarding page
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    const isOnboarding = window.location.pathname.includes('/onboarding')
    
    if (authToken && user && !isOnboarding) {
      // Start monitoring localStorage for changes
      this.startMonitoring()
      console.log('✅ GoogleTokenRefreshService: Initialized with rate limiting and monitoring')
    } else {
      if (isOnboarding) {
        console.log('🔒 GoogleTokenRefreshService: Onboarding detected, monitoring disabled')
      } else {
        console.log('🔒 GoogleTokenRefreshService: User not authenticated, monitoring disabled')
      }
    }
  }

  /**
   * Check if we should allow a refresh attempt (rate limiting)
   */
  shouldAllowRefresh() {
    const now = Date.now()
    
    // If we have no previous refresh attempts, always allow
    if (this.refreshAttempts.length === 0) {
      return true
    }
    
    // Check if we're within the minimum interval
    if (this.lastRefreshTime && (now - this.lastRefreshTime) < this.minRefreshInterval) {
      // If the last refresh was successful and recent, allow it (for retry scenarios)
      const lastAttempt = this.refreshAttempts[this.refreshAttempts.length - 1]
      if (lastAttempt && (now - lastAttempt) < 10000) { // Allow within 10 seconds if recent
        return true
      }
      return false
    }
    
    // Check if we've exceeded max attempts within the window
    if (this.refreshAttempts.length >= this.maxRefreshAttempts) {
      const oldestAttempt = this.refreshAttempts[0]
      if (now - oldestAttempt < this.refreshWindow) {
        return false
      }
      // Remove old attempts outside the window
      this.refreshAttempts = this.refreshAttempts.filter(
        attempt => (now - attempt) < this.refreshWindow
      )
    }
    
    return true
  }
  
  /**
   * Record a refresh attempt
   */
  recordRefresh() {
    this.lastRefreshTime = Date.now()
    this.refreshAttempts.push(this.lastRefreshTime)
    this.refreshHistory.push(this.lastRefreshTime)
    
    // Keep only attempts within the window
    const now = Date.now()
    this.refreshAttempts = this.refreshAttempts.filter(
      attempt => (now - attempt) < this.refreshWindow
    )
    
    // Keep only recent refreshes for status reporting
    this.refreshHistory = this.refreshHistory.filter(
      time => (now - time) < 60000 // Keep last minute
    )
  }

  /**
   * Use stored refresh token to get fresh access token from Google API via backend
   */
  async refreshTokensViaGoogleAPI() {
    try {
      // Check if we should allow this refresh
      if (!this.shouldAllowRefresh()) {
        throw new Error('Refresh not allowed due to rate limiting or timing restrictions')
      }

      console.log('🔄 GoogleTokenRefresh: Starting token refresh process via TokenManager')
      
      // IMPORTANT: Use TokenManager's refreshGoogleDriveToken instead of duplicating the API call
      // This ensures we have a single, coordinated refresh mechanism across the entire application
      try {
        // Import TokenManager dynamically to avoid circular dependencies
        const { default: tokenManager } = await import('./tokenManager.js')
        
        if (!tokenManager || typeof tokenManager.refreshGoogleDriveToken !== 'function') {
          throw new Error('TokenManager not available, falling back to direct API call')
        }
        
        console.log('🔄 GoogleTokenRefresh: Using TokenManager for coordinated refresh')
        const newAccessToken = await tokenManager.refreshGoogleDriveToken()
        
        // Update internal properties to maintain consistency
        this.accessToken = newAccessToken
        this.refreshToken = localStorage.getItem('cloudRefreshToken')
        
        // Record this successful refresh
        this.recordRefresh()
        
        console.log('✅ GoogleTokenRefresh: Token refresh successful via TokenManager')
        
        return {
          accessToken: newAccessToken,
          refreshToken: this.refreshToken,
          success: true
        }
        
      } catch (tokenManagerError) {
        console.warn('⚠️ GoogleTokenRefresh: TokenManager refresh failed, falling back to direct API call:', tokenManagerError.message)
        
        // Fallback to original implementation if TokenManager fails
        return await this.refreshTokensViaGoogleAPIFallback()
      }
      
      if (!refreshToken) {
        console.log('⚠️ GoogleTokenRefresh: No refresh token in localStorage, fetching from backend...')
        
        // Fetch refresh token from backend first
        const authToken = localStorage.getItem('authToken')
        if (!authToken) {
          throw new Error('No authentication token available')
        }
        
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
        const response = await fetch(`${API_BASE_URL}/api/cloud/google-drive/tokens`, {
          headers: {
            'Authorization': `Basic ${authToken}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (!response.ok) {
          throw new Error(`Failed to fetch tokens from backend: ${response.status}`)
        }
        
        const tokenData = await response.json()
        refreshToken = tokenData.refreshToken
        
        if (!refreshToken) {
          throw new Error('Backend did not return a refresh token')
        }
        
        console.log('✅ GoogleTokenRefresh: Retrieved refresh token from backend')
        
        // Store the refresh token locally for future use
        localStorage.setItem('cloudRefreshToken', refreshToken)
      }

      // Get auth token for backend authentication
      const authToken = localStorage.getItem('authToken')
      if (!authToken) {
        throw new Error('No authentication token available')
      }

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      
      console.log('🔄 GoogleTokenRefresh: Calling backend refresh endpoint')
      
      // Use refresh token to get fresh access token from Google API via backend
      console.log('🔍 GoogleTokenRefresh: Sending refresh request to backend...')
      console.log('🔍 GoogleTokenRefresh: Backend URL:', `${API_BASE_URL}/api/cloud/google-drive/refresh-token`)
      console.log('🔍 GoogleTokenRefresh: Auth token exists:', !!authToken)
      console.log('🔍 GoogleTokenRefresh: Refresh token exists:', !!refreshToken)
      console.log('🔍 GoogleTokenRefresh: Refresh token length:', refreshToken?.length)
      
      const refreshResponse = await fetch(`${API_BASE_URL}/api/cloud/google-drive/refresh-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      })

      if (!refreshResponse.ok) {
        if (refreshResponse.status === 400) {
          // 400 usually means invalid/expired refresh token
          const errorText = await refreshResponse.text()
          console.warn('⚠️ GoogleTokenRefresh: 400 Bad Request from refresh endpoint')
          console.warn('⚠️ GoogleTokenRefresh: This usually means the refresh token is invalid/expired')
          console.warn('⚠️ GoogleTokenRefresh: Error details:', errorText)
          
          // Clear the invalid refresh token from localStorage
          localStorage.removeItem('cloudRefreshToken')
          console.log('🧹 GoogleTokenRefresh: Removed invalid refresh token from localStorage')
          
          // Dispatch event to trigger re-authentication
          window.dispatchEvent(new CustomEvent('googleDriveTokenExpired', { 
            detail: { 
              reason: 'INVALID_REFRESH_TOKEN',
              message: 'Refresh token is invalid or expired. Please re-authenticate with Google Drive.',
              requiresReauth: true
            }
          }))
          
          throw new Error('Refresh token is invalid or expired. Please re-authenticate with Google Drive.')
        }
        
        const errorData = await refreshResponse.json().catch(() => ({}))
        console.error('❌ GoogleTokenRefresh: Backend refresh failed:', {
          status: refreshResponse.status,
          statusText: refreshResponse.statusText,
          error: errorData
        })
        throw new Error(`Failed to refresh token via Google API: ${refreshResponse.status} - ${errorData.error || 'Unknown error'}`)
      }

      const refreshData = await refreshResponse.json()
      console.log('✅ GoogleTokenRefresh: Backend refresh successful:', {
        success: refreshData.success,
        hasAccessToken: !!refreshData.accessToken,
        hasRefreshToken: !!refreshData.refreshToken,
        accessTokenLength: refreshData.accessToken?.length,
        refreshTokenLength: refreshData.refreshToken?.length
      })
      
      if (!refreshData.success) {
        throw new Error('Backend returned unsuccessful response for token refresh')
      }

      const newAccessToken = refreshData.accessToken
      const newRefreshToken = refreshData.refreshToken || refreshToken // Use new refresh token if provided, otherwise keep existing

      // Record this successful refresh
      this.recordRefresh()

      // Save fresh tokens to localStorage
      localStorage.setItem('cloudAccessToken', newAccessToken)
      localStorage.setItem('cloudRefreshToken', newRefreshToken)
      
      // CRITICAL: Also set the expiry in localStorage for proactive refresh
      const expiryTime = new Date(Date.now() + 45 * 60 * 1000) // 45 minutes - more conservative
      localStorage.setItem('googleDriveTokenExpiry', expiryTime.toISOString())
      
      // Update internal properties
      this.accessToken = newAccessToken
      this.refreshToken = newRefreshToken
      
      // Save additional provider info if provided in the response
      if (refreshData.userInfo) {
        if (refreshData.userInfo.id) {
          localStorage.setItem('cloudProviderUserId', refreshData.userInfo.id)
        }
        if (refreshData.userInfo.email) {
          localStorage.setItem('cloudProviderUserEmail', refreshData.userInfo.email)
        }
        if (refreshData.userInfo.name) {
          localStorage.setItem('cloudProviderUserName', refreshData.userInfo.name)
        }
      }

      console.log('✅ GoogleTokenRefresh: Tokens refreshed successfully')
      console.log(`🔑 GoogleTokenRefresh: New access token length: ${newAccessToken?.length || 0}`)
      console.log(`🔑 GoogleTokenRefresh: New refresh token length: ${newRefreshToken?.length || 0}`)
      
      // Reset retry count on successful refresh
      this.retryCount = 0
      
      return {
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        success: true
      }

    } catch (error) {
      console.error('❌ GoogleTokenRefresh: Failed to refresh tokens:', error)
      throw error
    }
  }

  /**
   * Fallback method for direct API refresh (original implementation)
   * This is kept as a safety net in case TokenManager is unavailable
   */
  async refreshTokensViaGoogleAPIFallback() {
    console.log('🔄 GoogleTokenRefresh: Using fallback direct API refresh')
    
    // Get stored refresh token from localStorage
    let refreshToken = localStorage.getItem('cloudRefreshToken')
    
    if (!refreshToken) {
      console.log('⚠️ GoogleTokenRefresh: No refresh token in localStorage, fetching from backend...')
      
      // Fetch refresh token from backend first
      const authToken = localStorage.getItem('authToken')
      if (!authToken) {
        throw new Error('No authentication token available')
      }
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/cloud/google-drive/tokens`, {
        headers: {
          'Authorization': `Basic ${authToken}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch tokens from backend: ${response.status}`)
      }
      
      const tokenData = await response.json()
      refreshToken = tokenData.refreshToken
      
      if (!refreshToken) {
        throw new Error('Backend did not return a refresh token')
      }
      
      console.log('✅ GoogleTokenRefresh: Retrieved refresh token from backend')
      
      // Store the refresh token locally for future use
      localStorage.setItem('cloudRefreshToken', refreshToken)
    }

    // Get auth token for backend authentication
    const authToken = localStorage.getItem('authToken')
    if (!authToken) {
      throw new Error('No authentication token available')
    }

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
    
    console.log('🔄 GoogleTokenRefresh: Calling backend refresh endpoint (fallback)')
    
    // Use refresh token to get fresh access token from Google API via backend
    console.log('🔍 GoogleTokenRefresh: Sending refresh request to backend...')
    console.log('🔍 GoogleTokenRefresh: Backend URL:', `${API_BASE_URL}/api/cloud/google-drive/refresh-token`)
    console.log('🔍 GoogleTokenRefresh: Auth token exists:', !!authToken)
    console.log('🔍 GoogleTokenRefresh: Refresh token exists:', !!refreshToken)
    console.log('🔍 GoogleTokenRefresh: Refresh token length:', refreshToken?.length)
    
    const refreshResponse = await fetch(`${API_BASE_URL}/api/cloud/google-drive/refresh-token`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        refresh_token: refreshToken
      })
    })

    if (!refreshResponse.ok) {
      if (refreshResponse.status === 400) {
        // 400 usually means invalid/expired refresh token
        const errorText = await refreshResponse.text()
        console.warn('⚠️ GoogleTokenRefresh: 400 Bad Request from refresh endpoint')
        console.warn('⚠️ GoogleTokenRefresh: This usually means the refresh token is invalid/expired')
        console.warn('⚠️ GoogleTokenRefresh: Error details:', errorText)
        
        // Clear the invalid refresh token from localStorage
        localStorage.removeItem('cloudRefreshToken')
        console.log('🧹 GoogleTokenRefresh: Removed invalid refresh token from localStorage')
        
        // Dispatch event to trigger re-authentication
        window.dispatchEvent(new CustomEvent('googleDriveTokenExpired', { 
          detail: { 
            reason: 'INVALID_REFRESH_TOKEN',
            message: 'Refresh token is invalid or expired. Please re-authenticate with Google Drive.',
            requiresReauth: true
          }
        }))
        
        throw new Error('Refresh token is invalid or expired. Please re-authenticate with Google Drive.')
      }
      
      const errorData = await refreshResponse.json().catch(() => ({}))
      console.error('❌ GoogleTokenRefresh: Backend refresh failed:', {
        status: refreshResponse.status,
        statusText: refreshResponse.statusText,
        error: errorData
      })
      throw new Error(`Failed to refresh token via Google API: ${refreshResponse.status} - ${errorData.error || 'Unknown error'}`)
    }

    const refreshData = await refreshResponse.json()
    console.log('✅ GoogleTokenRefresh: Backend refresh successful (fallback):', {
      success: refreshData.success,
      hasAccessToken: !!refreshData.accessToken,
      hasRefreshToken: !!refreshData.refreshToken,
      accessTokenLength: refreshData.accessToken?.length,
      refreshTokenLength: refreshData.refreshToken?.length
    })
    
    if (!refreshData.success) {
      throw new Error('Backend returned unsuccessful response for token refresh')
    }

    const newAccessToken = refreshData.accessToken
    const newRefreshToken = refreshData.refreshToken || refreshToken // Use new refresh token if provided, otherwise keep existing

    // Record this successful refresh
    this.recordRefresh()

    // Save fresh tokens to localStorage
    localStorage.setItem('cloudAccessToken', newAccessToken)
    localStorage.setItem('cloudRefreshToken', newRefreshToken)
    
    // CRITICAL: Also set the expiry in localStorage for proactive refresh
    const expiryTime = new Date(Date.now() + 45 * 60 * 1000) // 45 minutes - more conservative
    localStorage.setItem('googleDriveTokenExpiry', expiryTime.toISOString())
    
    // Update internal properties
    this.accessToken = newAccessToken
    this.refreshToken = newRefreshToken
    
    // Save additional provider info if provided in the response
    if (refreshData.userInfo) {
      if (refreshData.userInfo.id) {
        localStorage.setItem('cloudProviderUserId', refreshData.userInfo.id)
      }
      if (refreshData.userInfo.email) {
        localStorage.setItem('cloudProviderUserEmail', refreshData.userInfo.email)
      }
      if (refreshData.userInfo.name) {
        localStorage.setItem('cloudProviderUserName', refreshData.userInfo.name)
      }
    }

    console.log('✅ GoogleTokenRefresh: Tokens refreshed successfully (fallback)')
    console.log(`🔑 GoogleTokenRefresh: New access token length: ${newAccessToken?.length || 0}`)
    console.log(`🔑 GoogleTokenRefresh: New refresh token length: ${newRefreshToken?.length || 0}`)
    
    // Reset retry count on successful refresh
    this.retryCount = 0
    
    return {
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
      success: true
    }
  }

  /**
   * Handle 401 response from Google API with automatic token refresh
   */
  async handle401Response(originalRequest, retryCount = 0) {
    try {
      console.log(`🔄 GoogleTokenRefresh: Handling 401 response (attempt ${retryCount + 1}/${this.maxRetries + 1})`)

      if (retryCount > this.maxRetries) {
        console.error('❌ GoogleTokenRefresh: Max retries exceeded, cannot refresh tokens')
        throw new Error('Max token refresh retries exceeded')
      }

      // Check if we're already refreshing
      if (this.isRefreshing) {
        console.log('🔄 GoogleTokenRefresh: Token refresh already in progress, waiting...')
        return await this.refreshPromise
      }

      // Check if we should allow refresh (rate limiting)
      if (!this.shouldAllowRefresh()) {
        console.error('❌ GoogleTokenRefresh: Refresh not allowed due to rate limiting')
        throw new Error('Token refresh rate limited - too many attempts')
      }

      // Start refresh process
      this.isRefreshing = true
      this.refreshPromise = this.refreshTokensViaGoogleAPI()
      
      try {
        const result = await this.refreshPromise
        
        // Retry the original request with new token
        if (result.success && result.accessToken) {
          console.log('🔄 GoogleTokenRefresh: Retrying original request with new token')
          
          // Update the request with new token
          const updatedRequest = this.updateRequestWithNewToken(originalRequest, result.accessToken)
          
          // Retry the request
          const retryResponse = await fetch(updatedRequest.url, updatedRequest.options)
          
          if (retryResponse.status === 401) {
            // Still getting 401, try one more time but with caution
            console.log('🔄 GoogleTokenRefresh: Still getting 401, trying one more time')
            console.log('⚠️ GoogleTokenRefresh: This might indicate a persistent authentication issue')
            return await this.handle401Response(originalRequest, retryCount + 1)
          }
          
          return retryResponse
        }
        
        throw new Error('Token refresh failed')
        
      } finally {
        this.isRefreshing = false
        this.refreshPromise = null
      }

    } catch (error) {
      console.error('❌ GoogleTokenRefresh: Error handling 401 response:', error)
      this.isRefreshing = false
      this.refreshPromise = null
      throw error
    }
  }

  /**
   * Update request with new access token
   */
  updateRequestWithNewToken(originalRequest, newAccessToken) {
    const updatedRequest = { ...originalRequest }
    
    // Update Authorization header if it exists
    if (updatedRequest.options && updatedRequest.options.headers) {
      const headers = { ...updatedRequest.options.headers }
      
      // Check if it's a Google API request (Bearer token)
      if (headers.Authorization && headers.Authorization.startsWith('Bearer ')) {
        headers.Authorization = `Bearer ${newAccessToken}`
        console.log('🔑 GoogleTokenRefresh: Updated Authorization header with new token')
      }
      
      updatedRequest.options.headers = headers
    }
    
    return updatedRequest
  }

  /**
   * Create a fetch wrapper that automatically handles 401 responses
   */
  createFetchWrapper() {
    return async (url, options = {}) => {
      try {
        const response = await fetch(url, options)
        
        // If we get a 401, try to refresh tokens and retry
        if (response.status === 401) {
          console.log('🔄 GoogleTokenRefresh: Got 401 response, attempting token refresh')
          
          const originalRequest = { url, options }
          return await this.handle401Response(originalRequest)
        }
        
        return response
        
      } catch (error) {
        console.error('❌ GoogleTokenRefresh: Fetch error:', error)
        throw error
      }
    }
  }

  /**
   * Handle 401 response from Google APIs by refreshing tokens
   */
  async handleGoogleAPI401(originalRequest = null) {
    // CRITICAL: Check if user is authenticated before attempting token refresh
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (!authToken || !user) {
      console.log('🔒 GoogleTokenRefresh: User not authenticated, blocking token refresh')
      throw new Error('User not authenticated. Please log in first.')
    }
    
    // Check if we should allow a refresh attempt
    if (!this.shouldAllowRefresh()) {
      console.log('⏰ GoogleTokenRefresh: Refresh rate limited, blocking request')
      throw new Error('Token refresh rate limited. Please wait before trying again.')
    }
    
    console.log('🔄 GoogleTokenRefresh: Handling 401 response, attempting token refresh...')
    console.log('🔍 GoogleTokenRefresh: Current refresh token exists:', !!this.refreshToken)
    console.log('🔍 GoogleTokenRefresh: Current access token exists:', !!this.accessToken)
    
    try {
      const refreshResult = await this.refreshTokensViaGoogleAPI()
      console.log('🔍 GoogleTokenRefresh: Refresh result:', refreshResult)
      
      if (refreshResult && refreshResult.success && refreshResult.accessToken) {
        console.log('✅ GoogleTokenRefresh: Token refresh successful')
        this.recordRefresh()
        
        // Update global state and localStorage
        const newAccessToken = refreshResult.accessToken
        const newRefreshToken = refreshResult.refreshToken
        
        console.log('🔍 GoogleTokenRefresh: New access token length:', newAccessToken?.length)
        console.log('🔍 GoogleTokenRefresh: New refresh token length:', newRefreshToken?.length)
        console.log('🔍 GoogleTokenRefresh: New access token type:', typeof newAccessToken)
        console.log('🔍 GoogleTokenRefresh: New access token is string:', typeof newAccessToken === 'string')
        
        // Update localStorage
        localStorage.setItem('cloudAccessToken', newAccessToken)
        if (newRefreshToken) {
          localStorage.setItem('cloudRefreshToken', newRefreshToken)
        }
        
        // CRITICAL: Also set the expiry in localStorage for proactive refresh
        const expiryTime = new Date(Date.now() + 45 * 60 * 1000) // 45 minutes - more conservative
        localStorage.setItem('googleDriveTokenExpiry', expiryTime.toISOString())
        
        // Update internal properties
        this.accessToken = newAccessToken
        this.refreshToken = newRefreshToken
        
        // Dispatch event to notify other components about token refresh
        window.dispatchEvent(new CustomEvent('googleDriveTokenRefreshed', {
          detail: { 
            accessToken: newAccessToken, 
            refreshToken: newRefreshToken 
          }
        }))
        
        console.log('✅ GoogleTokenRefresh: Dispatched googleDriveTokenRefreshed event')
        console.log('🔍 GoogleTokenRefresh: About to return access token:', {
          token: newAccessToken,
          type: typeof newAccessToken,
          length: newAccessToken?.length,
          isString: typeof newAccessToken === 'string'
        })
        
        // Always return the access token string
        // The calling code should handle retrying the request with the new token
        return newAccessToken
      }
      
      console.error('❌ GoogleTokenRefresh: Invalid refresh result:', refreshResult)
      throw new Error('Failed to refresh Google Drive token')
      
    } catch (error) {
      console.error('❌ GoogleTokenRefresh: Token refresh failed:', error)
      
      // Clear invalid tokens
      this.clearInvalidTokens()
      
      throw error
    }
  }

  /**
   * Refresh tokens without retrying any specific request (for reconnection purposes)
   */
  async refreshTokensOnly() {
    // CRITICAL: Check if user is authenticated before attempting token refresh
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (!authToken || !user) {
      console.log('🔒 GoogleTokenRefresh: User not authenticated, blocking token refresh')
      throw new Error('User not authenticated. Please log in first.')
    }
    
    // Check if we should allow a refresh attempt
    if (!this.shouldAllowRefresh()) {
      console.log('⏰ GoogleTokenRefresh: Refresh rate limited, blocking request')
      throw new Error('Token refresh rate limited. Please wait before trying again.')
    }
    
    console.log('🔄 GoogleTokenRefresh: Refreshing tokens for reconnection...')
    
    try {
      const refreshResult = await this.refreshTokensViaGoogleAPI()
      
      if (refreshResult && refreshResult.success && refreshResult.accessToken) {
        console.log('✅ GoogleTokenRefresh: Token refresh successful for reconnection')
        this.recordRefresh()
        
        // Update global state and localStorage
        const newAccessToken = refreshResult.accessToken
        const newRefreshToken = refreshResult.refreshToken
        
        // Update localStorage
        localStorage.setItem('cloudAccessToken', newAccessToken)
        if (newRefreshToken) {
          localStorage.setItem('cloudRefreshToken', newRefreshToken)
        }
        
        // CRITICAL: Also set the expiry in localStorage for proactive refresh
        const expiryTime = new Date(Date.now() + 45 * 60 * 1000) // 45 minutes - more conservative
        localStorage.setItem('googleDriveTokenExpiry', expiryTime.toISOString())
        
        // Update internal properties
        this.accessToken = newAccessToken
        this.refreshToken = newRefreshToken
        
        // Dispatch event to notify other components about token refresh
        window.dispatchEvent(new CustomEvent('googleDriveTokenRefreshed', {
          detail: { 
            accessToken: newAccessToken, 
            refreshToken: newRefreshToken 
          }
        }))
        
        return newAccessToken
      }
      
      throw new Error('Failed to refresh Google Drive token')
      
    } catch (error) {
      console.error('❌ GoogleTokenRefresh: Token refresh for reconnection failed:', error)
      throw error
    }
  }
  
  /**
   * Clear invalid tokens from localStorage
   */
  clearInvalidTokens() {
    try {
      localStorage.removeItem('cloudAccessToken')
      localStorage.removeItem('cloudRefreshToken')
      
      // Clear internal properties
      this.accessToken = null
      this.refreshToken = null
      
      console.log('🧹 GoogleTokenRefresh: Cleared invalid tokens')
    } catch (error) {
      console.error('❌ GoogleTokenRefresh: Failed to clear invalid tokens:', error)
    }
  }

  /**
   * Reset the service state
   */
  reset() {
    console.log('🔄 GoogleTokenRefresh: Resetting service state')
    this.isRefreshing = false
    this.refreshPromise = null
    this.retryCount = 0
    this.lastRefreshTime = 0
    this.refreshCount = 0
    this.refreshHistory = []
    this.refreshAttempts = []
    this.accessToken = null
    this.refreshToken = null
  }

  /**
   * Manually trigger token refresh (for testing/debugging)
   */
  async manualRefresh() {
    console.log('🔄 GoogleTokenRefresh: Manual refresh triggered')
    
    try {
      const refreshResult = await this.refreshTokensViaGoogleAPI()
      
      if (refreshResult && refreshResult.success && refreshResult.accessToken) {
        console.log('✅ GoogleTokenRefresh: Manual refresh successful')
        
        // Update global state and localStorage
        const newAccessToken = refreshResult.accessToken
        const newRefreshToken = refreshResult.refreshToken
        
        // Update localStorage
        localStorage.setItem('cloudAccessToken', newAccessToken)
        if (newRefreshToken) {
          localStorage.setItem('cloudRefreshToken', newRefreshToken)
        }
        
        // CRITICAL: Also set the expiry in localStorage for proactive refresh
        const expiryTime = new Date(Date.now() + 45 * 60 * 1000) // 45 minutes - more conservative
        localStorage.setItem('googleDriveTokenExpiry', expiryTime.toISOString())
        
        // Update internal properties
        this.accessToken = newAccessToken
        this.refreshToken = newRefreshToken
        
        // Dispatch event to notify other components about token refresh
        window.dispatchEvent(new CustomEvent('googleDriveTokenRefreshed', {
          detail: { 
            accessToken: newAccessToken, 
            refreshToken: newRefreshToken 
          }
        }))
        
        return { success: true, accessToken: newAccessToken, refreshToken: newRefreshToken }
      }
      
      throw new Error('Manual refresh failed')
      
    } catch (error) {
      console.error('❌ GoogleTokenRefresh: Manual refresh failed:', error)
      throw error
    }
  }

  /**
   * Get current refresh status
   */
  getStatus() {
    const now = Date.now()
    const recentRefreshes = this.refreshHistory.filter(time => now - time < 60000).length
    
    return {
      isRefreshing: this.isRefreshing,
      retryCount: this.retryCount,
      maxRetries: this.maxRetries,
      lastRefreshTime: this.lastRefreshTime,
      timeSinceLastRefresh: this.lastRefreshTime ? now - this.lastRefreshTime : null,
      refreshCount: this.refreshCount,
      recentRefreshes,
      maxRefreshPerMinute: this.maxRefreshPerMinute,
      minRefreshInterval: this.minRefreshInterval,
      canRefresh: this.shouldAllowRefresh()
    }
  }

  /**
   * Start monitoring localStorage for token changes
   */
  startMonitoring() {
    if (this.monitoringStarted) {
      console.log('🔍 GoogleTokenRefresh: Monitoring already started')
      return
    }
    
    // Check if user is authenticated before starting monitoring
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (!authToken || !user) {
      console.log('🔒 GoogleTokenRefresh: Cannot start monitoring - user not authenticated')
      return
    }
    
    this.monitoringStarted = true
    this.originalSetItem = localStorage.setItem
    this.originalRemoveItem = localStorage.removeItem
    
    // Monitor when cloudRefreshToken is removed
    const self = this
    localStorage.removeItem = function(key) {
      if (key === 'cloudRefreshToken') {
        console.warn('⚠️ GoogleTokenRefresh: WARNING - cloudRefreshToken is being removed!')
        console.warn('⚠️ GoogleTokenRefresh: This will break automatic token refresh!')
        console.warn('⚠️ GoogleTokenRefresh: Stack trace:', new Error().stack)
        
        // Log current state before removal
        self.logTokenState()
      }
      
      // Call original method
      return self.originalRemoveItem.call(this, key)
    }
    
    console.log('🔍 GoogleTokenRefresh: Started monitoring localStorage for token changes')
  }

  /**
   * Stop monitoring localStorage
   */
  stopMonitoring() {
    if (!this.monitoringStarted) {
      return
    }
    
    localStorage.setItem = this.originalSetItem
    localStorage.removeItem = this.originalRemoveItem
    this.monitoringStarted = false
    
    console.log('🔍 GoogleTokenRefresh: Stopped monitoring localStorage')
  }

  /**
   * Enable monitoring when user logs in
   */
  enableMonitoring() {
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (authToken && user && !this.monitoringStarted) {
      console.log('🔍 GoogleTokenRefresh: Enabling monitoring for authenticated user')
      this.startMonitoring()
    }
  }

  /**
   * Disable monitoring when user logs out
   */
  disableMonitoring() {
    if (this.monitoringStarted) {
      console.log('🔍 GoogleTokenRefresh: Disabling monitoring for logged out user')
      this.stopMonitoring()
    }
  }

  /**
   * Check and update monitoring state based on authentication
   */
  checkAuthenticationState() {
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (authToken && user) {
      if (!this.monitoringStarted) {
        this.enableMonitoring()
      }
    } else {
      if (this.monitoringStarted) {
        this.disableMonitoring()
      }
    }
  }

  /**
   * Update internal token properties from localStorage
   */
  updateTokenProperties() {
    this.accessToken = localStorage.getItem('cloudAccessToken') || null
    this.refreshToken = localStorage.getItem('cloudRefreshToken') || null
  }

  /**
   * Sync internal state with localStorage
   */
  syncWithLocalStorage() {
    this.updateTokenProperties()
    this.checkAuthenticationState()
  }

  /**
   * Handle external token updates (e.g., from OAuth callback)
   */
  handleExternalTokenUpdate() {
    console.log('🔄 GoogleTokenRefresh: Handling external token update')
    this.syncWithLocalStorage()
  }

  /**
   * Emergency stop - disable all functionality
   */
  emergencyStop() {
    console.log('🚨 GoogleTokenRefresh: Emergency stop activated')
    this.stopMonitoring()
    this.isRefreshing = false
    this.refreshPromise = null
    this.retryCount = 0
    this.refreshCount = 0
    this.lastRefreshTime = null
    this.refreshAttempts = []
    this.refreshHistory = []
  }

  /**
   * Reset monitoring - restart monitoring if user is authenticated
   */
  resetMonitoring() {
    console.log('🔄 GoogleTokenRefresh: Resetting monitoring')
    this.stopMonitoring()
    this.checkAuthenticationState()
  }

  /**
   * Get current token state
   */
  getCurrentTokens() {
    return {
      accessToken: this.accessToken,
      refreshToken: this.refreshToken,
      hasAccessToken: !!this.accessToken,
      hasRefreshToken: !!this.refreshToken
    }
  }

  /**
   * Check if service is ready to handle requests
   */
  isReady() {
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    return !!(authToken && user && this.monitoringStarted)
  }

  /**
   * Get service health status
   */
  getHealthStatus() {
    return {
      isReady: this.isReady(),
      isMonitoring: this.monitoringStarted,
      isRefreshing: this.isRefreshing,
      hasTokens: this.getCurrentTokens(),
      canRefresh: this.shouldAllowRefresh(),
      lastRefresh: this.lastRefreshTime,
      refreshCount: this.refreshCount,
      retryCount: this.retryCount
    }
  }

  /**
   * Force refresh of internal state
   */
  forceRefresh() {
    console.log('🔄 GoogleTokenRefresh: Force refreshing internal state')
    this.syncWithLocalStorage()
    this.reset()
  }

  /**
   * Cleanup method for component unmounting
   */
  cleanup() {
    console.log('🧹 GoogleTokenRefresh: Cleaning up service')
    this.stopMonitoring()
    this.reset()
  }

  /**
   * Initialize service for authenticated user
   */
  initializeForUser() {
    console.log('🚀 GoogleTokenRefresh: Initializing service for authenticated user')
    this.syncWithLocalStorage()
  }

  /**
   * Deinitialize service for logged out user
   */
  deinitializeForUser() {
    console.log('🚪 GoogleTokenRefresh: Deinitializing service for logged out user')
    this.emergencyStop()
  }

  /**
   * Get service version and capabilities
   */
  getServiceInfo() {
    return {
      version: '2.0.0',
      capabilities: [
        'automatic_token_refresh',
        'rate_limiting',
        'localStorage_monitoring',
        'authentication_guards',
        'multi_device_support',
        'emergency_stop',
        'health_monitoring'
      ],
      lastUpdated: new Date().toISOString()
    }
  }

  /**
   * Validate service configuration
   */
  validateConfiguration() {
    const issues = []
    
    if (!this.minRefreshInterval || this.minRefreshInterval < 1000) {
      issues.push('minRefreshInterval too low')
    }
    
    if (!this.maxRefreshAttempts || this.maxRefreshAttempts < 1) {
      issues.push('maxRefreshAttempts too low')
    }
    
    if (!this.maxRetries || this.maxRetries < 1) {
      issues.push('maxRetries too low')
    }
    
    return {
      isValid: issues.length === 0,
      issues
    }
  }

  /**
   * Get comprehensive service status
   */
  getComprehensiveStatus() {
    return {
      service: this.getServiceInfo(),
      health: this.getHealthStatus(),
      configuration: this.validateConfiguration(),
      tokens: this.getCurrentTokens(),
      monitoring: {
        isActive: this.monitoringStarted,
        canStart: !!(localStorage.getItem('authToken') && localStorage.getItem('user'))
      },
      timestamp: new Date().toISOString()
    }
  }

  /**
   * Export service for debugging
   */
  exportForDebug() {
    return {
      ...this.getComprehensiveStatus(),
      internalState: {
        isRefreshing: this.isRefreshing,
        refreshPromise: this.refreshPromise ? 'Promise exists' : 'No promise',
        refreshAttempts: this.refreshAttempts,
        refreshHistory: this.refreshHistory,
        lastRefreshTime: this.lastRefreshTime
      }
    }
  }

  /**
   * Get service summary for logging
   */
  getSummary() {
    const status = this.getHealthStatus()
    return `GoogleTokenRefreshService: Ready=${status.isReady}, Monitoring=${status.isMonitoring}, Refreshing=${status.isRefreshing}, Tokens=${status.hasTokens.hasAccessToken ? 'Access' : 'None'}/${status.hasTokens.hasRefreshToken ? 'Refresh' : 'None'}`
  }

  /**
   * Log service status
   */
  logStatus() {
    console.log('📊 GoogleTokenRefresh: Service Status')
    console.log(this.getSummary())
    console.log('📊 GoogleTokenRefresh: Health Status', this.getHealthStatus())
    console.log('📊 GoogleTokenRefresh: Configuration', this.validateConfiguration())
  }

  /**
   * Get service metrics
   */
  getMetrics() {
    const now = Date.now()
    return {
      uptime: this.lastRefreshTime ? now - this.lastRefreshTime : 0,
      refreshRate: this.refreshHistory.length / 60, // refreshes per minute
      successRate: this.refreshCount > 0 ? (this.refreshCount - this.retryCount) / this.refreshCount : 1,
      averageRefreshInterval: this.refreshHistory.length > 1 ? 
        this.refreshHistory.slice(1).reduce((sum, time, i) => sum + (time - this.refreshHistory[i]), 0) / (this.refreshHistory.length - 1) : 0
    }
  }

      /**
   * Get service diagnostics
   */
  getDiagnostics() {
    return {
      status: this.getSummary(),
      health: this.getHealthStatus(),
      metrics: this.getMetrics(),
      configuration: this.validateConfiguration(),
      monitoring: {
        isActive: this.monitoringStarted,
        canStart: !!(localStorage.getItem('authToken') && localStorage.getItem('user')),
        localStorage: {
          hasAuthToken: !!localStorage.getItem('authToken'),
          hasUser: !!localStorage.getItem('user'),
          hasCloudAccessToken: !!localStorage.getItem('cloudAccessToken'),
          hasCloudRefreshToken: !!localStorage.getItem('cloudRefreshToken')
        }
      },
      timestamp: new Date().toISOString()
    }
  }

  /**
   * Log current token state for debugging
   */
  logTokenState() {
    const accessToken = localStorage.getItem('cloudAccessToken')
    const refreshToken = localStorage.getItem('cloudRefreshToken')
    const authToken = localStorage.getItem('authToken')
    
    console.log('🔍 GoogleTokenRefresh: Current token state:')
    console.log(`  - Access Token: ${accessToken ? `Present (${accessToken.length} chars)` : 'Missing'}`)
    console.log(`  - Refresh Token: ${refreshToken ? `Present (${refreshToken.length} chars)` : 'Missing'}`)
    console.log(`  - Auth Token: ${authToken ? `Present (${authToken.length} chars)` : 'Missing'}`)
    console.log(`  - Service Status:`, this.getStatus())
  }

  /**
   * Get detailed debug information
   */
  getDebugInfo() {
    const status = this.getStatus()
    const storedTokens = {
      hasAccessToken: !!localStorage.getItem('cloudAccessToken'),
      hasRefreshToken: !!localStorage.getItem('cloudRefreshToken'),
      accessTokenLength: localStorage.getItem('cloudAccessToken')?.length || 0,
      refreshTokenLength: localStorage.getItem('cloudRefreshToken')?.length || 0
    }
    
    return {
      serviceStatus: status,
      storedTokens,
      timestamp: new Date().toISOString()
    }
  }

  /**
   * Enable monitoring (useful after onboarding completion)
   */
  enableMonitoring() {
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (authToken && user) {
      this.startMonitoring()
      console.log('✅ GoogleTokenRefreshService: Monitoring enabled after onboarding')
    } else {
      console.log('🔒 GoogleTokenRefreshService: Cannot enable monitoring - user not authenticated')
    }
  }
}

// Export singleton instance
const googleTokenRefreshService = new GoogleTokenRefreshService()
export { googleTokenRefreshService }
export default googleTokenRefreshService
